/**
 * /tools/discovery-call?clientId=X — F-022 (2026-05-23).
 *
 * Display name: "Discovery Call / KPI Pre-Assessment".
 *
 * Step 0 baseline assessment captured during the discovery call. Rates
 * Energy / Work / Love / Identity Gap on 1..10 + "what stands between
 * your score and a 10?" per axis + core-pain narrative.
 *
 * Save behaviour (Michael 2026-05-23):
 *   - The save button is disabled (labelled "Saved") until the user
 *     makes a change — usePristine pattern.
 *   - On submit the form locks: the editable form is replaced by a
 *     read-only baseline view. Baselines are baselines — clients can't
 *     re-edit from the web. (If the coach needs to re-baseline, they
 *     can delete the row from prod and the form re-opens.)
 *
 * Visual: commitment-agreement family — dark ink hero header + flame-
 * left-border score cards + flame-glow detail callouts.
 *
 * Tool key: `discovery-call` (repurposed in F-022 from the old
 * narrative-only form). The legacy `kpi-pre-assessment` key is gone.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

type DomainKey = 'energy' | 'work' | 'love';

interface FormState {
  energyScore: number;
  workScore: number;
  loveScore: number;
  identityGapScore: number;
  energyGapText: string;
  workGapText: string;
  loveGapText: string;
  corePainText: string;
}

const DEFAULT_FORM: FormState = {
  energyScore: 0,
  workScore: 0,
  loveScore: 0,
  identityGapScore: 0,
  energyGapText: '',
  workGapText: '',
  loveGapText: '',
  corePainText: '',
};

export function KpiPreAssessmentPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const { isPristine, markSaved } = usePristine(DEFAULT_FORM);

  /* Hydrate from any prior submission. If a baseline exists, the page
     locks immediately — `savedAt` flips on and the read-only view
     renders instead of the editable form. */
  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'discovery-call');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Partial<FormState> & Record<string, unknown>;
          const next: FormState = {
            ...DEFAULT_FORM,
            ...(typeof p.energyScore === 'number' ? { energyScore: p.energyScore } : {}),
            ...(typeof p.workScore === 'number' ? { workScore: p.workScore } : {}),
            ...(typeof p.loveScore === 'number' ? { loveScore: p.loveScore } : {}),
            ...(typeof p.identityGapScore === 'number'
              ? { identityGapScore: p.identityGapScore }
              : {}),
            ...(typeof p.energyGapText === 'string' ? { energyGapText: p.energyGapText } : {}),
            ...(typeof p.workGapText === 'string' ? { workGapText: p.workGapText } : {}),
            ...(typeof p.loveGapText === 'string' ? { loveGapText: p.loveGapText } : {}),
            ...(typeof p.corePainText === 'string' ? { corePainText: p.corePainText } : {}),
          };
          setForm(next);
          markSaved(next);
          setSavedAt(existing.completed_at ?? new Date().toISOString());
        }
      } catch {
        /* swallow — fresh form */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, markSaved]);

  const dirty = !isPristine(form);
  const hasRequired =
    form.energyScore >= 1 &&
    form.workScore >= 1 &&
    form.loveScore >= 1 &&
    form.identityGapScore >= 1;
  const canSubmit = useMemo(
    () => !submitting && hydrated && hasRequired && dirty,
    [submitting, hydrated, hasRequired, dirty],
  );

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.kpi.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.kpi.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      energyScore: form.energyScore,
      workScore: form.workScore,
      loveScore: form.loveScore,
      identityGapScore: form.identityGapScore,
      ...(form.energyGapText.trim() ? { energyGapText: form.energyGapText.trim() } : {}),
      ...(form.workGapText.trim() ? { workGapText: form.workGapText.trim() } : {}),
      ...(form.loveGapText.trim() ? { loveGapText: form.loveGapText.trim() } : {}),
      ...(form.corePainText.trim() ? { corePainText: form.corePainText.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'discovery-call',
        { payload },
      );
      markSaved(form);
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.kpi.errors.generic'));
      } else {
        setSubmitError(t('tools.kpi.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="kpi-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.kpi.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.kpi.errors.missingClient')}</p>
      </section>
    );
  }

  /* Once a baseline exists the page locks — read-only view replaces the
     editable form. Per Michael's directive: baselines can't be edited
     from the client web. */
  if (hydrated && savedAt) {
    return (
      <BaselineSavedView form={form} savedAt={savedAt} />
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="kpi">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      {/* Hero header — matches the Client Commitment Agreement style. */}
      <header className="mb-s6 rounded-xl bg-ink text-bone p-s6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-flame-glow">
          {t('tools.kpi.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-flame mt-s2">{t('tools.kpi.title')}</h1>
        <p className="mt-s2 text-sm text-bone/70 max-w-prose mx-auto">{t('tools.kpi.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s6"
        noValidate
      >
        <section className="space-y-s4">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.kpi.section.scores')}
          </h2>
          <DomainCard
            domain="energy"
            score={form.energyScore}
            gapText={form.energyGapText}
            onScoreChange={(v) => setForm((prev) => ({ ...prev, energyScore: v }))}
            onGapTextChange={(v) => setForm((prev) => ({ ...prev, energyGapText: v }))}
          />
          <DomainCard
            domain="work"
            score={form.workScore}
            gapText={form.workGapText}
            onScoreChange={(v) => setForm((prev) => ({ ...prev, workScore: v }))}
            onGapTextChange={(v) => setForm((prev) => ({ ...prev, workGapText: v }))}
          />
          <DomainCard
            domain="love"
            score={form.loveScore}
            gapText={form.loveGapText}
            onScoreChange={(v) => setForm((prev) => ({ ...prev, loveScore: v }))}
            onGapTextChange={(v) => setForm((prev) => ({ ...prev, loveGapText: v }))}
          />
        </section>

        <section className="space-y-s4">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.kpi.section.identity')}
          </h2>
          <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm">
            <h3 className="font-display text-lg text-ink">
              {t('tools.kpi.identityGap.title')}
            </h3>
            <p className="mt-s2 text-sm text-ink italic">{t('tools.kpi.identityGap.prompt')}</p>
            <ScoreScale
              value={form.identityGapScore}
              onChange={(v) => setForm((prev) => ({ ...prev, identityGapScore: v }))}
              testId="kpi-identity-scale"
              ariaLabel={t('tools.kpi.identityGap.title')}
            />
            <div className="flex justify-between text-xs text-gray-1 mt-s2">
              <span>{t('tools.kpi.identityGap.lowLabel')}</span>
              <span>{t('tools.kpi.identityGap.highLabel')}</span>
            </div>
          </article>
        </section>

        <section className="space-y-s4">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.kpi.section.corePain')}
          </h2>
          <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm">
            <h3 className="font-display text-lg text-ink">{t('tools.kpi.corePain.title')}</h3>
            <p className="mt-s2 text-sm text-ink italic">{t('tools.kpi.corePain.prompt')}</p>
            <textarea
              value={form.corePainText}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, corePainText: event.target.value }))
              }
              placeholder={t('tools.kpi.corePain.placeholder')}
              className="input-field mt-s3 min-h-24"
              data-testid="kpi-core-pain"
              rows={5}
            />
          </article>
        </section>

        {submitError && (
          <p role="alert" className="form-error" data-testid="kpi-submit-error">
            {submitError}
          </p>
        )}

        <FinalizeWarning />

        <button
          type="submit"
          className={
            canSubmit
              ? 'btn-primary w-full'
              : 'btn-primary w-full opacity-50 cursor-not-allowed'
          }
          disabled={!canSubmit}
          data-testid="kpi-submit"
        >
          {submitting
            ? t('tools.kpi.submitting')
            : dirty
              ? t('tools.kpi.submit')
              : t('tools.kpi.saved')}
        </button>
      </form>
    </section>
  );
}

/* ───────────── Read-only baseline view (post-save lockout) ───────────── */

function BaselineSavedView({ form, savedAt }: { form: FormState; savedAt: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="kpi-baseline">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6 rounded-xl bg-ink text-bone p-s6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-flame-glow">
          {t('tools.kpi.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-flame mt-s2">{t('tools.kpi.title')}</h1>
        <p className="mt-s2 text-sm text-bone/70 max-w-prose mx-auto">
          {t('tools.kpi.baseline.subtitle')}
        </p>
      </header>

      <article
        className="mb-s5 rounded-md border border-flame/50 bg-flame-glow/30 p-s4"
        role="status"
        data-testid="kpi-baseline-banner"
      >
        <p className="text-sm text-ink">
          ✓ {t('tools.kpi.baseline.savedOn', { when: savedAt.slice(0, 10) })}
        </p>
        <p className="mt-s1 text-xs text-ink/80">{t('tools.kpi.baseline.locked')}</p>
      </article>

      <section className="space-y-s4">
        <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
          {t('tools.kpi.section.scores')}
        </h2>
        <ReadOnlyDomainCard domain="energy" score={form.energyScore} gapText={form.energyGapText} />
        <ReadOnlyDomainCard domain="work" score={form.workScore} gapText={form.workGapText} />
        <ReadOnlyDomainCard domain="love" score={form.loveScore} gapText={form.loveGapText} />
      </section>

      <section className="space-y-s4 mt-s6">
        <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
          {t('tools.kpi.section.identity')}
        </h2>
        <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm">
          <h3 className="font-display text-lg text-ink">{t('tools.kpi.identityGap.title')}</h3>
          <div className="mt-s3 flex items-baseline gap-s2">
            <span className="font-display text-display-xl text-flame-deep">{form.identityGapScore}</span>
            <span className="text-sm text-gray-2">/ 10</span>
          </div>
        </article>
      </section>

      {form.corePainText.trim().length > 0 && (
        <section className="space-y-s4 mt-s6">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.kpi.section.corePain')}
          </h2>
          <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm">
            <h3 className="font-display text-lg text-ink">{t('tools.kpi.corePain.title')}</h3>
            <p className="mt-s3 text-base text-ink italic whitespace-pre-wrap">
              {form.corePainText}
            </p>
          </article>
        </section>
      )}
    </section>
  );
}

function ReadOnlyDomainCard({
  domain,
  score,
  gapText,
}: {
  domain: DomainKey;
  score: number;
  gapText: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm">
      <h3 className="font-display text-lg text-ink">{t(`tools.kpi.${domain}.title`)}</h3>
      <div className="mt-s3 flex items-baseline gap-s2">
        <span className="font-display text-display-xl text-flame-deep">{score}</span>
        <span className="text-sm text-gray-2">/ 10</span>
      </div>
      {gapText.trim().length > 0 && (
        <p className="mt-s3 rounded-md bg-flame-glow/30 p-s3 text-sm text-ink/90 italic whitespace-pre-wrap">
          {gapText}
        </p>
      )}
    </article>
  );
}

/* ───────────── Editable card primitives ───────────── */

interface DomainCardProps {
  domain: DomainKey;
  score: number;
  gapText: string;
  onScoreChange: (value: number) => void;
  onGapTextChange: (value: string) => void;
}

function DomainCard({
  domain,
  score,
  gapText,
  onScoreChange,
  onGapTextChange,
}: DomainCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <article
      className="rounded-md border-l-4 border-flame bg-bone-2 p-s5 shadow-sm"
      data-testid={`kpi-domain-${domain}`}
    >
      <h3 className="font-display text-lg text-ink">{t(`tools.kpi.${domain}.title`)}</h3>
      <p className="mt-s2 text-sm text-ink italic">{t(`tools.kpi.${domain}.question`)}</p>
      <p className="mt-s1 text-xs text-gray-1">{t(`tools.kpi.${domain}.hint`)}</p>
      <ScoreScale
        value={score}
        onChange={onScoreChange}
        testId={`kpi-${domain}-scale`}
        ariaLabel={t(`tools.kpi.${domain}.title`)}
      />
      <div className="flex justify-between text-xs text-gray-1 mt-s2">
        <span>{t('tools.kpi.scaleLabels.low')}</span>
        <span>{t('tools.kpi.scaleLabels.mid')}</span>
        <span>{t('tools.kpi.scaleLabels.high')}</span>
      </div>
      <div className="mt-s4">
        <label htmlFor={`kpi-${domain}-gap`} className="form-label">
          {t('tools.kpi.gapPrompt')}
        </label>
        <textarea
          id={`kpi-${domain}-gap`}
          value={gapText}
          onChange={(event) => onGapTextChange(event.target.value)}
          placeholder={t('tools.kpi.gapPlaceholder')}
          className="input-field mt-s2 min-h-20"
          data-testid={`kpi-${domain}-gap`}
          rows={3}
        />
      </div>
    </article>
  );
}

interface ScoreScaleProps {
  value: number;
  onChange: (value: number) => void;
  testId: string;
  ariaLabel: string;
}

function ScoreScale({ value, onChange, testId, ariaLabel }: ScoreScaleProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="flex flex-wrap gap-s2 mt-s3"
      data-testid={testId}
    >
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
        const active = n === value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(n)}
            data-testid={`${testId}-${n}`}
            className={
              active
                ? 'h-10 w-10 rounded-full bg-flame text-bone font-medium'
                : 'h-10 w-10 rounded-full border border-bone-3 text-gray-2 hover:border-flame hover:text-ink'
            }
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}
